import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Home, MessageSquare, User, HelpCircle, Menu } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { NotificationBell } from "./NotificationBell";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { routes } from "@/lib/routes";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";

export const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  
  const isOnDashboard = location.pathname === routes.dashboard();

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchUnreadCount();
      subscribeToMessages();
    }
  }, [userId]);

  const fetchUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUserId(user?.id || null);
  };

  const fetchUnreadCount = async () => {
    if (!userId) return;

    const { data: conversations } = await supabase
      .from("direct_conversations")
      .select("id")
      .or(`learner_id.eq.${userId},coach_id.eq.${userId}`);

    if (!conversations) return;

    const conversationIds = conversations.map(c => c.id);
    
    const { count } = await supabase
      .from("direct_messages")
      .select("*", { count: "exact", head: true })
      .in("conversation_id", conversationIds)
      .eq("read", false)
      .neq("sender_id", userId);

    setUnreadMessages(count || 0);
  };

  const subscribeToMessages = () => {
    const channel = supabase
      .channel("header-messages")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "direct_messages",
        },
        () => {
          fetchUnreadCount();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "direct_messages",
        },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center">
        <div className="mr-4 flex">
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11"
            onClick={() => navigate(routes.home())}
          >
            <Home className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex flex-1 items-center justify-end space-x-1 sm:space-x-2">
          {/* Desktop navigation */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(routes.helpCenter())}
            className="hidden sm:flex items-center gap-2 h-11 px-4"
          >
            <HelpCircle className="h-5 w-5" />
            {t('nav.faq')}
          </Button>

          {!isOnDashboard && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(routes.dashboard())}
              className="hidden sm:flex items-center gap-2 h-11 px-4"
            >
              <User className="h-5 w-5" />
              {t('nav.dashboard')}
            </Button>
          )}

          <LanguageSwitcher />

          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 relative"
            onClick={() => navigate(routes.messages())}
          >
            <MessageSquare className="h-5 w-5" />
            {unreadMessages > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-xs"
              >
                {unreadMessages > 9 ? "9+" : unreadMessages}
              </Badge>
            )}
          </Button>

          <NotificationBell />

          {/* Mobile menu */}
          {isMobile && (
            <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 sm:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </DrawerTrigger>
              <DrawerContent>
                <DrawerHeader>
                  <DrawerTitle>{t('nav.menu')}</DrawerTitle>
                </DrawerHeader>
                <nav className="flex flex-col p-4 space-y-2">
                  <Button
                    variant="ghost"
                    className="justify-start h-12 text-base"
                    onClick={() => handleNavigation(routes.helpCenter())}
                  >
                    <HelpCircle className="h-5 w-5 mr-3" />
                    {t('nav.faq')}
                  </Button>
                  {!isOnDashboard && (
                    <Button
                      variant="ghost"
                      className="justify-start h-12 text-base"
                      onClick={() => handleNavigation(routes.dashboard())}
                    >
                      <User className="h-5 w-5 mr-3" />
                      {t('nav.dashboard')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="justify-start h-12 text-base"
                    onClick={() => handleNavigation(routes.messages())}
                  >
                    <MessageSquare className="h-5 w-5 mr-3" />
                    {t('nav.messages')}
                    {unreadMessages > 0 && (
                      <Badge variant="destructive" className="ml-auto">
                        {unreadMessages > 9 ? "9+" : unreadMessages}
                      </Badge>
                    )}
                  </Button>
                </nav>
              </DrawerContent>
            </Drawer>
          )}
        </div>
      </div>
    </header>
  );
};